import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import styles from './EmblaCarousel.module.css'
import classNames from 'classnames'
import { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { IconColor } from '@/atoms/utils'
import { useEmblaAutoplay } from '@/molecules/EmblaCarousel/useEmblaAutoPlay'

interface Props extends PropsWithChildren {
  className?: string
  containerClassName?: string
  options?: EmblaOptionsType
  plugins?: EmblaPluginType[]
  pauseAutoplay?: boolean
  includeArrows?: boolean
  leftArrowClassname?: string
  rightArrowClassname?: string
  iconColor?: IconColor
  includeGhostSlide?: boolean
  ghostSlideClassName?: string
}
export const EmblaCarousel = ({
  className,
  containerClassName,
  options = {},
  plugins = [],
  pauseAutoplay,
  children,
  includeGhostSlide,
  ghostSlideClassName,
  includeArrows,
  leftArrowClassname,
  rightArrowClassname,
  iconColor = 'white',
}: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins)
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const { autoplayIsPlaying, toggleAutoplay, isReady } = useEmblaAutoplay(emblaApi)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (!isReady) return
    if (pauseAutoplay && autoplayIsPlaying) toggleAutoplay()
    if (!pauseAutoplay && !autoplayIsPlaying) toggleAutoplay()
  }, [autoplayIsPlaying, isReady, pauseAutoplay, toggleAutoplay])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    if (!includeArrows) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, includeArrows, onSelect])

  return (
    <div className={classNames(className, 'relative')}>
      <div ref={emblaRef} className="w-full">
        <div className={classNames(styles.embla__container, containerClassName)}>
          {children}
          {includeGhostSlide && (
            <div className={classNames(styles.embla__slide, 'w-full opacity-0', ghostSlideClassName)} />
          )}
        </div>
      </div>

      {!prevBtnDisabled && (
        <button
          className={classNames('absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full p-4', leftArrowClassname)}
          onClick={scrollPrev}
        >
          <CaretLeftIcon className="md:size-8" color={iconColor} size={24} />
        </button>
      )}

      {!nextBtnDisabled && (
        <button
          className={classNames('absolute right-2 top-1/2 -translate-y-1/2 z-20 rounded-full p-4', rightArrowClassname)}
          onClick={scrollNext}
        >
          <CaretRightIcon className="md:size-8" color={iconColor} size={24} />
        </button>
      )}
    </div>
  )
}
